import React, { useState, useContext } from 'react';
import Personnummer from 'personnummer';

// API
import { activateCGI, loginWithoutCGI } from '../../api';

// Components
import { LoginDevInput } from '../../components/CGI/input';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { Layout } from '../../components/Layout';
import { ErrorMessage } from '../../components/ErrorMessage';
import InputDropdown from '../../components/InputDropdown';
import LoginNoEIdForm from '../../components/LoginNoEIdForm';

// i18n
import { useTranslation } from '../../translations/useTranslation';

// Utils
import { DebugContext } from '../../utilities/debugContext';
import { bisnodeResultToContactInformation } from '../../utilities/utils';
import { rebaseQuestionForMember } from '../../utilities/questionnaire';
import {
  isYoungEnough,
  isPersonalNumberValid,
  getPersonAge,
} from '../../utilities/person';

// Context
import { useForm } from '../../context/form-context/hooks';
import {
  setAuthenticationType,
  setAutostartLink,
  setCgiProvider,
  setContactInformation,
  setFormScene,
  setFormViewState,
  setOrderRef,
  setQrStartToken,
  setRequestStartedMilliseconds,
  setUnion,
  setUserAge,
} from '../../context/form-context/actions';
import { CgiAuthenticationType, CgiProvider } from '../../interfaces/CGI';

// Interfaces
import { InputSelectOption } from '../../interfaces/Input';

export const StartView: React.FC = () => {
  // States
  const { state, dispatch } = useForm();
  const [personalIdForDev, setPersonalIdForDev] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<CgiProvider>(
    CgiProvider.BankId
  );
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Misc hooks
  const { debugEnabled } = useContext(DebugContext);
  const { t, keys, locale } = useTranslation();

  const handleOnPersonalNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setPersonalIdForDev(value);
    setIsValid(null);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPersonalIdForDev(value.trim());
  };

  const authOptions: InputSelectOption[] = [
    {
      value: CgiProvider.BankId,
      label: t(keys.cgiBankId),
    },
    {
      value: CgiProvider.Freja,
      label: t(keys.cgiFreja),
    },
  ];

  const getAutoStartLink = (
    autoStartToken: string | undefined,
    provider: CgiProvider
  ): string | undefined => {
    switch (provider) {
      case CgiProvider.Freja:
        return `frejaeid://bindUserToTransaction?transactionReference=${autoStartToken}&originAppScheme=null`;
      case CgiProvider.BankId:
        return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
      default:
        return undefined;
    }
  };

  const handleOnLogin = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    const authType: CgiAuthenticationType =
      event.currentTarget.id === 'same-device' ? 'same-device' : 'other-device';
    const cgiProvider = selectedProvider;
    setIsLoading(true);
    activateCGI(cgiProvider)
      .then((response) => {
        setIsLoading(false);
        if (dispatch) {
          dispatch(setCgiProvider(cgiProvider));
          dispatch(setAuthenticationType(authType));
          dispatch(
            setAutostartLink(
              getAutoStartLink(response.data.autoStartToken, cgiProvider)
            )
          );
          dispatch(setOrderRef(response.data.orderRef));
          dispatch(
            setRequestStartedMilliseconds(
              response.data.requestStartedMilliseconds
            )
          );
          dispatch(
            setQrStartToken(
              cgiProvider === CgiProvider.BankId
                ? response.data.qrStartToken
                : response.data.autoStartToken
            )
          );
          dispatch(setFormScene('WAITING-FOR-SIGNATURE'));
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setIsValid(null);
        console.error(error);
      });
  };

  const handleOnLoginNoEId = (value: string) => {
    if (dispatch) {
      dispatch(
        setContactInformation({
          firstName: '',
          familyName: '',
          street: '',
          postalCode: '',
          city: '',
          email: '',
          emailRepeated: '',
          phoneNumber: '',
          country: '',
          personalIdentityNumber: value,
        })
      );
      dispatch(setFormScene('FORM'));
    }
  };

  const handleOnDebugLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = isPersonalNumberValid(personalIdForDev);
    const isYoung = isYoungEnough(personalIdForDev);
    const age = getPersonAge(personalIdForDev);

    if (isValid && isYoung && !isLoading) {
      const pin = new Personnummer(personalIdForDev);
      const longFormat = pin.format(true);
      setIsLoading(true);
      loginWithoutCGI(longFormat)
        .then((result) => {
          setIsValid(null);
          setIsLoading(false);
          if (dispatch) {
            const contactInfo = bisnodeResultToContactInformation(
              result.data.bisnodeResult
            );
            contactInfo.personalIdentityNumber =
              result.data.personalIdentityNumber || longFormat;
            dispatch(setContactInformation(contactInfo));
            if (
              result.data.aipResult &&
              Object.keys(result.data.aipResult).length > 0
            ) {
              dispatch(setUnion(result.data?.aipResult?.union));
              dispatch(
                setFormViewState({
                  questionnaire: rebaseQuestionForMember(
                    state.formViewState.questionnaire
                  ),
                })
              );
              dispatch(setFormScene('ALREADY-A-MEMBER'));
            } else {
              dispatch(setFormScene('FORM'));
            }
          }
        })
        .catch((error) => {
          setIsLoading(false);
          setIsValid(null);
          console.error(error);
        });
    } else if (isValid && !isYoung) {
      setIsValid(null);
      setIsLoading(false);
      if (dispatch) {
        dispatch(setUserAge(age));
        dispatch(setFormScene('TOO-OLD'));
      }
    } else if (!isValid) {
      setIsValid(false);
    }
  };

  const handleAuthOptionChange = (value: string | undefined) => {
    if (value) {
      setSelectedProvider(value as CgiProvider);
    }
  };

  if (state.formMode === 'loading') {
    return null;
  }

  return (
    <Layout viewId={'start'}>
      <h1 className="text-2xl text-center margin-bottom-2xs">
        {t(keys.uiStartTitle)}
      </h1>
      {state.formMode === 'eId' && (
        <div className="flex flex-column gap-3xs">
          <h2 className="text-md letter-spacing-reset text-center font-primary font-medium">
            {t(keys.uiBeginLegimitize)}
          </h2>
          <div className="flex flex-column justify-center gap-3xs width-100% width-60%@md items-center margin-auto">
            <InputDropdown
              wrapperClassName="width-100%"
              choices={authOptions}
              value={selectedProvider}
              onChange={handleAuthOptionChange}
            />
            <div className="display@md width-100%">
              <Button
                id="other-device"
                className="width-100%"
                title={t(keys.uiLogin)}
                onClick={handleOnLogin}
                isLoading={isLoading}
              />
            </div>
            <div className="hide@md width-100%">
              <Button
                id="same-device"
                className="width-100%"
                title={t(keys.uiLogin)}
                onClick={handleOnLogin}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {state.formMode === 'no-eId' && (
        <div className="flex flex-column gap-xs">
          <h2 className='text-md letter-spacing-reset text-center font-primary font-medium'>{t(keys.uiBeginLegimitizeNoEid)}</h2>
          <LoginNoEIdForm onSubmit={handleOnLoginNoEId} isLoading={isLoading} />
        </div>
      )}

      {(debugEnabled && state.formMode !== 'no-eId') && (
        <form onSubmit={handleOnDebugLogin} className="flex flex-column justify-center gap-3xs width-100% width-60%@md items-center margin-auto margin-top-sm">
          <div className='width-100%'>
            <LoginDevInput
              value={personalIdForDev}
              onBlur={handleOnBlur}
              onChange={handleOnPersonalNumberChange}
            />
            {!personalIdForDev && isValid === false ? (
              <ErrorMessage message={t(keys.errorFillInRequiredFields)} />
            ) : null}
            {personalIdForDev && isValid === false ? (
              <ErrorMessage message={t(keys.errorInvalidPersonalID)} />
            ) : null}
          </div>
          <Button
            title={t(keys.uiLogin) + ' (debug mode)'}
            className="width-100%"
            type="submit"
            isLoading={isLoading}
          />
        </form>
      )}
      <div className="text-center text-component margin-top-sm">
        <p>
          {state.formMode === 'eId' && (
            <>
              {t(keys.noBankId)}{' '}
              <a
                href={
                  locale === 'en'
                    ? 'https://akademikernasakassa.se/nobankid'
                    : 'https://akademikernasakassa.se/utanbankid'
                }
                title={t(keys.noBankId)}
                target="_blank"
              >
                {t(keys.uiClickHere)}
              </a>
              <br />
            </>
          )}

          <a
            href="https://www.akademikernasakassa.se/gdpr"
            target="_blank"
            title={t(keys.policyLinkTitle)}
          >
            {t(keys.policyLinkTitle)}
          </a>
        </p>
      </div>
    </Layout>
  );
};
